import Keycloak from "keycloak-js";
import eventBus from "./eventbus";

export let keycloak: Keycloak;
export let keycloakInit: Promise<boolean>;

// A gate that exists immediately at module load. Anything needing Keycloak
// awaits this instead of checking whether it happens to be ready yet.
let resolveReady: () => void;
const keycloakReady: Promise<void> = new Promise((resolve) => {
  resolveReady = resolve;
});

/**
 * The realm roles on the current token, or none at all.
 *
 * Note the optional chaining on `keycloak` itself: the binding above is only assigned by
 * initializeKeycloak, so it is genuinely undefined until a session has been established, and
 * reading `.tokenParsed` off it throws rather than answering undefined. Anything that asks about
 * roles outside the authenticated shell - or in a test - would otherwise take the page down.
 *
 * No roles is the safe answer: it grants nothing.
 */
export const getUserRoles = (): string[] => keycloak?.tokenParsed?.realm_access?.roles ?? [];

/**
 * Whether the signed-in user holds a realm role.
 *
 * For deciding what to *offer*, never for deciding what is allowed - the service enforces every
 * one of these itself, and a token is a claim the browser cannot verify.
 */
export const hasRealmRole = (role: string): boolean => getUserRoles().includes(role);

/** Why the session ended, for the banner the landing page shows on the way back in. */
export type LogoutReason = 'logged-out' | 'expired';

const LOGOUT_REASON_KEY = 'cas.logoutReason';

/**
 * Log out, leaving a note for the landing page.
 *
 * The reason travels in sessionStorage rather than in the redirect URL. Logging out is a full
 * navigation out to Keycloak and back, so nothing held in memory survives it - but a query
 * parameter long outlives the banner it drives, sitting in the address bar, the history entry
 * and any link the user shares, and Keycloak hands it back again after the next login because
 * it defaults the post-login redirect to whatever URL the login was started from. A tab-scoped
 * key that is read once lasts exactly as long as the message is wanted.
 */
export const logoutWithReason = (reason: LogoutReason) => {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    // Storage blocked or full costs the banner, never the logout itself.
  }
  keycloak.logout({ redirectUri: `${window.location.origin}/` });
};

/**
 * Why the last logout happened, or null if the user simply arrived at the page.
 *
 * A plain read, deliberately: clearing is left to clearLogoutReason so that a lazy initialiser
 * run twice by StrictMode cannot throw the reason away before it is rendered.
 */
export const readLogoutReason = (): LogoutReason | null => {
  const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
  return reason === 'logged-out' || reason === 'expired' ? reason : null;
};

/** Drop the note, so a reload of the landing page is not told the news a second time. */
export const clearLogoutReason = () => sessionStorage.removeItem(LOGOUT_REASON_KEY);

export const initializeKeycloak = (config: { url: string; realm: string; clientId: string }) => {
  if (keycloak) {
    console.log("Keycloak is already initialized, skipping...");
    return keycloakInit;
  }

  keycloak = new Keycloak({
    url: config.url,
    realm: config.realm,
    clientId: config.clientId,
  });

  keycloak.onTokenExpired = () => {
    console.warn("Access token expired, attempting background refresh...");
    keycloak.updateToken(30).catch(() => {
      console.error("Refresh token also expired. Session is dead.");
      eventBus.emit('sessionExpired', null);
      logoutWithReason('expired');
    });
  };

  keycloakInit = keycloak.init({
    onLoad: 'check-sso',
    checkLoginIframe: false,
    pkceMethod: "S256",
  });

  // Open the gate once init settles — resolve either way so waiters aren't
  // stranded forever; they'll then hit the authenticated check below.
  keycloakInit
    .then((authenticated) => {
      if (authenticated) eventBus.emit('userAuthenticated', keycloak);
    })
    .catch((err) => console.error("Keycloak init failed", err))
    .finally(() => resolveReady());

  return keycloakInit;
};

/** Thrown when there's simply no session — not a transient failure. */
export class NotAuthenticatedError extends Error {
  constructor() {
    super("User not authenticated");
    this.name = "NotAuthenticatedError";
  }
}

export async function getAccessToken(): Promise<string> {
  await keycloakReady;          // waits for initializeKeycloak, however late it runs
  await keycloakInit;

  if (!keycloak?.authenticated) {
    throw new NotAuthenticatedError();
  }

  try {
    await keycloak.updateToken(30);
    return keycloak.token!;
  } catch (err) {
    console.error("Token refresh failed", err);
    logoutWithReason('expired');
    throw err;
  }
}